import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { useCompanyListSelectorData } from '~/client/src/shared/components/CompanyListSelector'
import { useCanEditResource } from '~/client/src/shared/hooks/useCanEditResource'
import { IControlFields } from '~/client/src/shared/hooks/useResourceControls'
import UserProject from '~/client/src/shared/models/UserProject'
import type { IResourceValueRecord } from '~/client/src/shared/resources/definitions'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import ResourceControlCascadeConfirmDialog from './ResourceControlCascadeConfirmDialog'
import {
  ResourceControlsTabContext,
  ResourceControlsTabContextValue,
} from './ResourceControlsTabContext'
import { useResourceControlsDraft } from './useResourceControlsDraft'

interface ProvidedStores {
  companiesStore: CompaniesStore
  projectTradesStore: ProjectTradesStore
  userProjectsStore: UserProjectsStore
  permitTypesStore: WorkflowTypesStore
  closuresStore: ClosuresStore
  projectDateStore: ProjectDateStore
  state: InitialState
  isMobileEnv: boolean
}

interface IProps {
  recordId: string
  /** Decides, with the current user, whether the controls are editable (canEditResource). */
  record: IResourceValueRecord | null
  resourceTypeId: string
  projectId: string
  controls: readonly string[]
  controlCommands: IResourceControlCommands
  controlFields: IControlFields
  onClose: () => void
  /** Supplied by desktop panels to make the inherited-hours chip's link active. */
  onEditProjectHours?: () => void
  /**
   * Whether this record is the project's site root, which inherits the project's closures.
   * Passed in rather than derived: the provider holds no record, while the card bodies that
   * mount it already classify the kind for the control list.
   */
  isProjectLocation?: boolean
  children: React.ReactNode
}

/**
 * Hosts the Controls-tab draft for one resource and shares it (plus the store-derived
 * display data the cells need) with both the tab content and the pinned Cancel/Save
 * footer. Wrap the panel body so the footer — a sibling of the tab's scroll area —
 * can read Save/Cancel. Also renders the cascade-confirmation dialog.
 */
function ResourceControlsTabProviderInner({
  recordId,
  record,
  resourceTypeId,
  projectId,
  controls,
  controlCommands,
  controlFields,
  onClose,
  onEditProjectHours,
  isProjectLocation,
  children,
}: IProps) {
  const {
    companiesStore,
    projectTradesStore,
    userProjectsStore,
    permitTypesStore,
    closuresStore,
    projectDateStore,
    state,
    isMobileEnv,
  } = React.useContext(MobXProviderContext) as unknown as ProvidedStores

  const { companies, companyMetadata } = useCompanyListSelectorData({
    companiesStore,
    projectTradesStore,
  })

  // Controls follow the resource's properties: its owners and admins may change them
  // (canEditResource, mirrored by the backend's controls mutations); everyone else
  // sees the tab read-only. Guard the user before isAdmin -> getByUser, which
  // destructures its argument.
  const canEdit = useCanEditResource(resourceTypeId)
  const readOnly = !canEdit(record)
  const user = state?.user
  const isAdmin = !!user && UserProject.isAdmin(user, userProjectsStore)

  const draft = useResourceControlsDraft({
    recordId,
    projectId,
    controls,
    controlCommands,
    controlFields,
    closuresStore,
    companies,
    onClose,
  })

  // `draft` gets a fresh identity each render, so a useMemo here could never
  // cache — build the value inline. `observer` already
  // scopes re-renders to the stores this reads, and the panel edits one resource.
  const value: ResourceControlsTabContextValue = {
    ...draft,
    recordId,
    resourceTypeId,
    readOnly,
    companies,
    companyMetadata,
    getCompanyById: companiesStore.getCompanyById,
    // Resolve any (possibly superseded) stored id to its current version so chips show
    // the latest workflow-type name.
    getPermitTypeById: permitTypesStore.getDisplayTypeByAnyVersionId,
    allWorkflowTypeIds: permitTypesStore.actualTypeIds,
    projectDateStore,
    // Project settings are admin-only, so the "manage in project settings" link is
    // too, even for an owner who can edit the controls (and absent on surfaces that
    // don't supply it, e.g. mobile).
    onEditProjectHours: isAdmin ? onEditProjectHours : undefined,
    // The mobile build points the chip to the desktop version instead of a link;
    // sourced from the global platform flag rather than threaded from the panel.
    isMobile: isMobileEnv,
    // The project's closures already apply project-wide on the backend; the site root shows
    // them so the cell states the rule rather than leaving it invisible.
    inheritedClosures: isProjectLocation
      ? (state?.activeProject?.closedIntervals ?? undefined)
      : undefined,
  }

  return (
    <ResourceControlsTabContext.Provider value={value}>
      {children}
      <ResourceControlCascadeConfirmDialog
        isOpen={!!draft.cascade}
        cascade={draft.cascade}
        isCommitting={draft.isCommittingCascade}
        onConfirm={draft.onConfirmCascade}
        onCancel={draft.onCancelCascade}
      />
    </ResourceControlsTabContext.Provider>
  )
}

export const ResourceControlsTabProvider = observer(
  ResourceControlsTabProviderInner,
)
